'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useCart } from '@/context/CartContext';
import { useWishlist } from '@/context/WishlistContext';
import { useAuth } from '@/context/AuthContext';
import { BagIcon, ChevronDownIcon, CloseIcon, HeartIcon, MenuIcon, SearchIcon, UserIcon } from './Icons';
import { HELP_LINKS, POPULAR_SEARCHES, PRIMARY_NAV, SHOP_CATEGORIES, SHOP_OCCASIONS } from './nav';

function Wordmark({ compact = false }: { compact?: boolean }) {
  return (
    <Link href="/" className="group flex items-center gap-2.5" aria-label="Aarohi Luxe — home">
      <Image src="/brand/logo-mark.webp" alt="" width={40} height={40} className={compact ? 'h-8 w-8' : 'h-9 w-9 lg:h-11 lg:w-11'} priority />
      <span className={`font-al-display leading-none tracking-[0.02em] ${compact ? 'text-[22px]' : 'text-[24px] lg:text-[30px]'}`}>
        <span className="text-al-gold">Aarohi</span> <span className="text-al-cream">Luxe</span>
      </span>
    </Link>
  );
}

function CountBadge({ count }: { count: number }) {
  if (!count) return null;
  return (
    <span className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-al-rose px-1 text-[10px] font-semibold text-al-ink">
      {count > 99 ? '99+' : count}
    </span>
  );
}

export default function SiteHeader() {
  const pathname = usePathname();
  const router = useRouter();
  const { itemCount } = useCart();
  const { itemCount: wishCount } = useWishlist();
  const { user } = useAuth();
  const [megaOpen, setMegaOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const megaTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close overlays on navigation
  useEffect(() => {
    setMegaOpen(false);
    setSearchOpen(false);
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMegaOpen(false);
        setSearchOpen(false);
        setDrawerOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  const openMega = () => {
    if (megaTimer.current) clearTimeout(megaTimer.current);
    setMegaOpen(true);
  };
  const closeMega = () => {
    megaTimer.current = setTimeout(() => setMegaOpen(false), 120);
  };

  const submitSearch = (term: string) => {
    const t = term.trim();
    if (!t) return;
    setSearchOpen(false);
    router.push(`/products?q=${encodeURIComponent(t)}`);
  };

  const accountHref = user ? '/account' : '/login';

  return (
    <header className="sticky top-0 z-50 border-b border-al-line/70 bg-al-ink/95 font-al-body text-al-cream backdrop-blur supports-[backdrop-filter]:bg-al-ink/85">
      {/* Row 1: search · logo · account/wishlist/bag */}
      <div className="al-container grid h-16 grid-cols-[1fr_auto_1fr] items-center lg:h-20">
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="-ml-2 rounded p-2 text-al-cream/85 hover:text-al-gold lg:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
          >
            <MenuIcon size={22} />
          </button>
          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            className="flex items-center gap-2 rounded p-2 text-al-cream/85 hover:text-al-gold"
            aria-label="Search"
            aria-expanded={searchOpen}
          >
            <SearchIcon size={20} />
            <span className="hidden text-[11px] uppercase tracking-[0.2em] lg:inline">Search</span>
          </button>
        </div>

        <Wordmark />

        <nav className="flex items-center justify-end gap-1 sm:gap-2" aria-label="Account">
          <Link href={accountHref} className="hidden rounded p-2 text-al-cream/85 hover:text-al-gold sm:block" aria-label={user ? 'Your account' : 'Sign in'}>
            <UserIcon size={21} />
          </Link>
          <Link href="/account/wishlist" className="relative hidden rounded p-2 text-al-cream/85 hover:text-al-gold sm:block" aria-label={`Wishlist, ${wishCount} items`}>
            <HeartIcon size={21} />
            <CountBadge count={wishCount} />
          </Link>
          <Link href="/cart" className="relative rounded p-2 text-al-cream/85 hover:text-al-gold" aria-label={`Shopping bag, ${itemCount} items`}>
            <BagIcon size={22} />
            <CountBadge count={itemCount} />
          </Link>
        </nav>
      </div>

      {/* Row 2: primary nav (desktop) */}
      <nav className="relative hidden border-t border-al-line/50 lg:block" aria-label="Main">
        <ul className="al-container flex h-12 items-center justify-center gap-9 text-[12px] uppercase tracking-[0.2em]">
          <li onMouseEnter={openMega} onMouseLeave={closeMega}>
            <button
              type="button"
              className="group flex items-center gap-1.5 uppercase tracking-[0.2em] text-al-cream/85 transition-colors hover:text-al-gold"
              aria-expanded={megaOpen}
              aria-controls="al-mega"
              onClick={() => setMegaOpen((v) => !v)}
              onFocus={openMega}
            >
              Shop
              <ChevronDownIcon size={14} className={`transition-transform ${megaOpen ? 'rotate-180' : ''}`} />
            </button>
          </li>
          {PRIMARY_NAV.map((item) => (
            <li key={item.label}>
              <Link
                href={item.href}
                className={`relative py-1 transition-colors after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-left after:scale-x-0 after:bg-al-gold after:transition-transform hover:text-al-gold hover:after:scale-x-100 ${
                  'accent' in item && item.accent ? 'text-al-gold' : 'text-al-cream/85'
                }`}
              >
                {item.label}
              </Link>
            </li>
          ))}
          <li>
            <Link href="/products?occasion=wedding" className="text-al-cream/85 transition-colors hover:text-al-gold">
              Bridal
            </Link>
          </li>
        </ul>

        {/* Mega menu */}
        <div
          id="al-mega"
          onMouseEnter={openMega}
          onMouseLeave={closeMega}
          className={`absolute inset-x-0 top-full border-y border-al-line/70 bg-al-ink-soft shadow-2xl shadow-black/40 transition-all duration-200 ${
            megaOpen ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-1 opacity-0'
          }`}
        >
          <div className="al-container grid grid-cols-[1.4fr_1fr_1.1fr] gap-12 py-10">
            <div>
              <p className="al-eyebrow">Shop by category</p>
              <ul className="mt-5 grid grid-cols-2 gap-x-8 gap-y-4">
                {SHOP_CATEGORIES.map((c) => (
                  <li key={c.href}>
                    <Link href={c.href} className="group block">
                      <span className="font-al-display text-[22px] text-al-cream transition-colors group-hover:text-al-gold">{c.label}</span>
                      <span className="mt-0.5 block text-[11px] normal-case tracking-normal text-al-cream/50">{c.note}</span>
                    </Link>
                  </li>
                ))}
                <li>
                  <Link href="/products" className="al-link mt-2 inline-block">Shop all</Link>
                </li>
              </ul>
            </div>
            <div>
              <p className="al-eyebrow">Shop by occasion</p>
              <ul className="mt-5 space-y-3">
                {SHOP_OCCASIONS.map((o) => (
                  <li key={o.href}>
                    <Link href={o.href} className="font-al-display text-[20px] text-al-cream/90 transition-colors hover:text-al-gold">
                      {o.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <Link href="/products?category=sarees" className="al-tile al-tile-scrim group relative block aspect-[4/3] self-start">
              <Image src="/images/aarohi/banarasi-edit.webp" alt="" fill sizes="30vw" className="object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute inset-x-0 bottom-0 z-10 p-6">
                <span className="al-eyebrow">The Banarasi Edit</span>
                <span className="mt-2 block font-al-display text-3xl normal-case tracking-normal text-al-cream">Woven in Varanasi</span>
              </span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Search panel */}
      <div className={`absolute inset-x-0 top-full border-b border-al-line/70 bg-al-ink-soft shadow-2xl shadow-black/40 ${searchOpen ? 'block' : 'hidden'}`}>
        <div className="al-container py-8 lg:py-10">
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              submitSearch(query);
            }}
            className="flex items-center gap-4 border-b border-al-cream/20 pb-3 focus-within:border-al-gold"
          >
            <SearchIcon size={24} className="text-al-gold" />
            <label htmlFor="al-search" className="sr-only">Search the store</label>
            <input
              id="al-search"
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search sarees, lehengas, chikankari…"
              className="w-full bg-transparent font-al-display text-2xl text-al-cream placeholder:text-al-cream/35 focus:outline-none lg:text-4xl"
            />
            <button type="button" onClick={() => setSearchOpen(false)} className="p-1 text-al-cream/60 hover:text-al-gold" aria-label="Close search">
              <CloseIcon size={22} />
            </button>
          </form>
          <div className="mt-5 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-al-cream/60">
            <span className="mr-2">Popular:</span>
            {POPULAR_SEARCHES.map((s) => (
              <button key={s} type="button" onClick={() => submitSearch(s)} className="rounded-full border border-al-cream/15 px-3 py-1.5 hover:border-al-gold hover:text-al-gold">
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-[60] lg:hidden ${drawerOpen ? '' : 'pointer-events-none'}`} aria-hidden={!drawerOpen}>
        <div
          className={`absolute inset-0 bg-black/60 transition-opacity ${drawerOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setDrawerOpen(false)}
        />
        <aside
          className={`absolute inset-y-0 left-0 flex w-[86%] max-w-sm flex-col overflow-y-auto bg-al-ink px-6 pb-10 pt-5 transition-transform duration-300 ${
            drawerOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          aria-label="Menu"
        >
          <div className="flex items-center justify-between">
            <Wordmark compact />
            <button type="button" onClick={() => setDrawerOpen(false)} className="p-2 text-al-cream/70 hover:text-al-gold" aria-label="Close menu">
              <CloseIcon size={22} />
            </button>
          </div>
          <div className="al-rule-left mt-5" />
          <Link href="/products?sort=new" className="mt-6 font-al-display text-2xl text-al-gold">New In</Link>
          <p className="al-eyebrow mt-8">Categories</p>
          <ul className="mt-3 space-y-3">
            {SHOP_CATEGORIES.map((c) => (
              <li key={c.href}>
                <Link href={c.href} className="font-al-display text-2xl text-al-cream">{c.label}</Link>
              </li>
            ))}
          </ul>
          <p className="al-eyebrow mt-8">Occasions</p>
          <ul className="mt-3 grid grid-cols-2 gap-3 text-sm text-al-cream/80">
            {SHOP_OCCASIONS.map((o) => (
              <li key={o.href}><Link href={o.href}>{o.label}</Link></li>
            ))}
          </ul>
          <div className="al-rule-left mt-8" />
          <ul className="mt-6 space-y-3 text-sm text-al-cream/70">
            <li><Link href={accountHref}>{user ? 'My account' : 'Sign in / Register'}</Link></li>
            <li><Link href="/account/wishlist">Wishlist {wishCount ? `(${wishCount})` : ''}</Link></li>
            {HELP_LINKS.map((h) => (
              <li key={h.label}><Link href={h.href}>{h.label}</Link></li>
            ))}
          </ul>
        </aside>
      </div>
    </header>
  );
}
